/**
 * Progressive Image Loader
 * Loads images from data-src when visible
 */

const CLOUD_FLARE_IMAGE_VARIANT_ALIASES = {
  public: "gallery",
  thumbnail: "galleryThumb",
  gallery_thumb: "galleryThumb",
  content_sm: "contentSm",
  content_md: "contentMd",
  content_lg: "contentLg",
  card_wide: "cardWide",
  hero_md: "heroMd",
  hero_lg: "heroLg",
  cover: "heroMd",
  "100x100": "galleryThumb",
  "200X200": "galleryThumb",
  "480X320Cover": "cardWide",
};

const NormalizeCloudflareImageVariant = (variant = "") => {
  const cleanVariant = String(variant || "").replace(/^\/+|\/+$/g, "");

  return CLOUD_FLARE_IMAGE_VARIANT_ALIASES[cleanVariant] || cleanVariant;
};

const IsCloudflareImageUrl = (url = "") => {
  const rawUrl = String(url || "");
  const cloudPhotoUrl = typeof CLOUD_PHOTO_URL !== "undefined" ? String(CLOUD_PHOTO_URL || "") : "";

  return rawUrl.includes("/cdn-cgi/imagedelivery/") || rawUrl.includes("imagedelivery.net/") || (cloudPhotoUrl && rawUrl.startsWith(cloudPhotoUrl));
};

const NormalizeCloudflareImageUrl = (url = "", variant = "") => {
  const rawUrl = String(url || "").trim();
  if (!rawUrl) {
    return rawUrl;
  }

  if (!IsCloudflareImageUrl(rawUrl)) {
    return rawUrl;
  }

  const cleanUrl = rawUrl.replace(/\/+$/, "");
  const parts = cleanUrl.split("/");
  const lastPart = parts[parts.length - 1] || "";
  const suffix = lastPart.match(/[?#].*$/)?.[0] || "";
  const lastVariant = lastPart.replace(/[?#].*$/, "");
  const normalizedLastVariant = NormalizeCloudflareImageVariant(lastVariant);

  if (lastVariant && normalizedLastVariant !== lastVariant) {
    parts[parts.length - 1] = normalizedLastVariant + suffix;
    return parts.join("/");
  }

  const normalizedVariant = NormalizeCloudflareImageVariant(variant);
  if (normalizedVariant && rawUrl.endsWith("/")) {
    return `${cleanUrl}/${normalizedVariant}`;
  }

  return rawUrl;
};

const createImageWithSpinner = ({ src, alt, width, height, wrapperClassName,className, defaultImage, wrapper_height, wrapper_width, wrapper_margin, errorImageWidth = "50%", circle = false, delay = 0 }) => {
  src = NormalizeCloudflareImageUrl(src);
  defaultImage = NormalizeCloudflareImageUrl(defaultImage || CLOUD_PHOTO_URL + "aip_Icon-100/gallery", "gallery");
  wrapper_height ??= "100%";
  wrapper_width ??= "100%";
  wrapper_margin ??= "";

  const style = document.createElement("style");
  style.textContent = `
    .photo-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;
      margin: ${wrapper_margin};
      height: ${wrapper_height};
      width: ${wrapper_width};
    }
    .photo-wrapper img.hidden,
    .photo-wrapper .skeleton.hidden,
    .photo-wrapper .spinner-grow.hidden {
      display: none;
    }
    .photo-wrapper img.visible,
    .photo-wrapper .skeleton.visible,
    .photo-wrapper .spinner-grow.visible {
      display: block;
    }
    .photo-wrapper img.visible {
      transition: opacity 0.3s ease-in-out;
      opacity: 1;
    }
    .photo-wrapper .skeleton {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, #dddddd 25%, #f5f5f5 50%, #dddddd 75%);
      background-size: 200% 100%;
      animation: shimmer 2.5s infinite;
      z-index: 1;
    }
    @keyframes shimmer {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }
    .spinner-grow {
      z-index: 2;
    }
  `;
  document.head.appendChild(style);

  const img = document.createElement("img");
  img.src = src;
  img.alt = alt || "";
  img.className = className || "";
  if (width) img.style.width = width;
  else img.style.width = "auto";
  if (height) img.style.height = height;
  else img.style.height = "auto";

  const wrapper = document.createElement("div");
  wrapper.classList.add("photo-wrapper", "position-relative");
  wrapper.classList.add(...(wrapperClassName?.split(/\s+/) || []));

  const skeleton = document.createElement("div");
  if (circle) skeleton.style.clipPath = "circle()";
  skeleton.classList.add("skeleton", "visible");
  wrapper.appendChild(skeleton);

  img.classList.add("hidden");
  wrapper.appendChild(img);

  img.onload = () => {
    setTimeout(
      () => {
        skeleton.classList.add("hidden");
        img.classList.remove("hidden");
        img.classList.add("visible");
        skeleton.classList.remove("visible");
        skeleton.remove();
      },
      delay > 0 ? delay + 300 : 300
    );
  };

  img.onerror = () => {
    img.src = NormalizeCloudflareImageUrl(defaultImage, "gallery");
    skeleton.classList.add("hidden");
    img.classList.remove("hidden");
    img.classList.add("visible");
    skeleton.remove();
    img.style.setProperty("width", errorImageWidth ?? "50%", "important");
    img.style.setProperty("height", "100%", "important");
    img.style.setProperty("object-fit", "contain", "important");
    img.dataset.error = true;
    img.onerror = null;
  };

  return wrapper;
};

/**
 * Create a responsive <img> element with full attributes and fallback
 * @param {Object} options
 * @param {string} options.src - Main image URL
 * @param {Object|string|null} [options.srcset] - Responsive sources (object or string)
 * @param {string|null} [options.sizes] - Sizes attribute
 * @param {string|null} [options.alt] - Alt text
 * @param {string} [options.loading="lazy"] - Loading strategy
 * @param {string} [options.className=""] - Extra CSS classes
 * @param {string|null} [options.id] - Element ID
 * @param {string} [options.fallback="/assets/images/default.png"] - Default fallback image
 * @param {HTMLElement|null} [options.parentElement=null] - Parent element for spinner/skeleton handling
 * @param {string|null} [options.style] - Inline styles for the <img> element
 * @param {string|null} [options.fallbackStyle] - Inline styles for the fallback image
 * @returns {HTMLImageElement} - The constructed <img> element
 */
BuildImageElement = function ({
  src,
  srcset = null,
  sizes = null,
  style = null,
  alt = "",
  loading = "lazy",
  className = "",
  id = null,
  fallback = CLOUD_PHOTO_URL + "aip_Icon-100/gallery",
  fallbackStyle = null,
  parentElement = null,
}) {
  if (!src) {
    throw new Error("BuildImageElement: 'src' is required");
  }

  const img = document.createElement("img");
  let skeleton, spinner;
  if (parentElement) {
    skeleton = parentElement?.querySelector(".skeleton-item");
    spinner = parentElement?.querySelector(".loading-spinner");
  }

  img.src = NormalizeCloudflareImageUrl(src);
  img.alt = alt || "";
  img.loading = loading;

  if (id) img.id = id;
  if (className) img.className = className;

  // Build srcset if provided
  if (srcset) {
    if (typeof srcset === "string") {
      img.setAttribute("srcset", srcset);
    } else if (typeof srcset === "object") {
      const srcsetString = Object.entries(srcset)
          .map(([size, url]) => `${NormalizeCloudflareImageUrl(url)} ${size}w`)
        .join(", ");
      img.setAttribute("srcset", srcsetString);
    }
  }

  // Apply sizes if provided
  if (sizes) {
    img.setAttribute("sizes", sizes);
  }

  if (style) {
    style.split(";").forEach((rule) => {
      const [key, value] = rule.split(":").map((s) => s.trim());
      if (key && value) {
        const cleanValue = value.replace("!important", "").trim();
        img.style.setProperty(key, cleanValue, "important");
      }
    });
  }

  img.onload = () => {
    if (skeleton) skeleton.remove();
    if (spinner) spinner.remove();
  };

  // Fallback handling
  img.onerror = () => {
    img.src = NormalizeCloudflareImageUrl(fallback, "gallery");
    img.removeAttribute("srcset");
    img.removeAttribute("sizes");
    if (skeleton) skeleton.remove();
    if (spinner) spinner.remove();
    img.onerror = null;
    if (fallbackStyle) {
      fallbackStyle.split(";").forEach((rule) => {
        const [key, value] = rule.split(":").map((s) => s.trim());
        if (key && value) {
          const cleanValue = value.replace("!important", "").trim();
          img.style.setProperty(key, cleanValue, "important");
        }
      });
    }
  };

  return img;
};

const HandleProgressiveImageLoad = (img, errorImage = "", styleString = "") => {
  const wrapper = img.closest(".image-wrapper");

  let highResSrc = img.getAttribute("data-original-src");
  highResSrc ||= img.getAttribute("data-mdb-img");
  highResSrc = NormalizeCloudflareImageUrl(highResSrc);
  if (img.dataset.loaded === "true") return;

  const spinner = wrapper?.querySelector(".loading-spinner");
  const skeleton = wrapper?.querySelector(".skeleton-item");

  const highResImg = new Image();
  highResImg.src = highResSrc;

  highResImg.onload = async function () {
    await new Promise((resolve) => setTimeout(resolve, 300));
    if (img.dataset.loaded === "true") return;

    img.src = highResSrc;
    img.dataset.loaded = "true";

    img.classList.add("loaded");
    img.classList.remove("hidden");
    img.classList.add("visible");
    spinner?.classList.add("hidden");
    skeleton?.classList.add("hidden");

    setTimeout(() => {
      spinner?.remove();
      skeleton?.remove();
    }, 300);
  };

  highResImg.onerror = function () {
    if (img.dataset.errorHandled === "true") return;
    img.dataset.errorHandled = "true";
    errorImage = NormalizeCloudflareImageUrl(errorImage || `${CLOUD_PHOTO_URL}aip_broken-image-light/gallery`, "gallery");
    img.src = errorImage;

    if (wrapper && styleString) {
      styleString.split(";").forEach((rule) => {
        const [key, value] = rule.split(":").map((s) => s.trim());
        if (key && value) {
          const cleanValue = value.replace("!important", "").trim();
          img.style.setProperty(key, cleanValue, "important");
        }
      });
      wrapper.style.textAlign = "center";
    }

    img.classList.add("loaded");
    img.classList.remove("hidden");
    img.classList.add("visible");
    spinner?.classList.add("hidden");
    skeleton?.classList.add("hidden");
    img.onerror = null;
    setTimeout(() => {
      spinner?.remove();
      skeleton?.remove();
    }, 300);
  };
};



function initResponsiveImages(selector = "") {
  const images = document.querySelectorAll(selector);

  images.forEach((img) => {
    img.onerror = () => {
      if (img.dataset.error) {
        img.src = img.dataset.error;
        img.removeAttribute("srcset");
        img.classList.remove("blur-image");
        img.classList.add("error-loaded");
        img.onerror = null;
        const skeleton = img.closest(".image-wrapper")?.querySelector(".skeleton-item");
        if (skeleton) skeleton.remove();
      }
    };

    img.onload = () => {
      img.classList.add("loaded");
      img.classList.remove("blur-image");
      const skeleton = img.closest(".image-wrapper")?.querySelector(".skeleton-item");
      if (skeleton) skeleton.remove();
    };

    if (img.complete) {
      if (img.naturalWidth === 0) {
        img.onerror();
      } else {
        img.onload();
      }
    }
  });
}


/**
 * HandleImageLoad: removes the loading spinner and skeleton from an image element
 * @param {HTMLImageElement} img - the image element
 */
function HandleImageLoad(img) {
  const wrapper = img.closest(".image-wrapper");
  let spinner = null;
  let skeleton = null;
  if (wrapper) {
    spinner = wrapper.querySelector(".loading-spinner");
    skeleton = wrapper.querySelector(".skeleton-item");
  }

  setTimeout(() => {
    spinner?.classList.add("hidden");
    spinner?.remove();
    skeleton?.classList.add("hidden");
    skeleton?.remove();
    img.classList.add("visible");
  }, 300);
}
/**
 * HandleErrorImage: display fallback image with optional styles
 * @param {HTMLImageElement} img - the image element
 * @param {string} [src] - the fallback image URL
 * @param {string} [element_selector] - the closest element selector which contains the image
 * @param {string} [styleString] - the CSS styles to apply to the image
 */

function HandleErrorImage(img, src = "", element_selector = ".image-wrapper", styleString = "") {
  src ||= `${SITE_ASSETS_URL}img/logo.png`;
  src = NormalizeCloudflareImageUrl(src, "gallery");
  const wrapper = img.closest(element_selector);
  let spinner = null;
  let skeleton = null;

  if (wrapper) {
    spinner = wrapper.querySelector(".loading-spinner");
    skeleton = wrapper.querySelector(".skeleton-item");
  }

  setTimeout(() => {
    if (wrapper && styleString) {
      styleString.split(";").forEach((rule) => {
        const [key, value] = rule.split(":").map((s) => s.trim());
        if (key && value) {
          const cleanValue = value.replace("!important", "").trim();
          img.style.setProperty(key, cleanValue, "important");
        }
      });
    }

    img.onerror = null;
    img.src = src;
    img.classList.add("visible");
    img.style.objectFit = "cover";

    spinner?.classList.add("hidden");
    spinner?.remove();
    skeleton?.classList.add("hidden");
    skeleton?.remove();
  }, 300);
}
